import { FC, useEffect, useState, } from "react";
import { EOBsObject } from "src/pages/EOBsDetail/types";
import { format, parse } from "date-fns";
import ViewPdf from "../viewPdf";
import { formatInsuranceType } from "src/helpers/downloadFile";
import { formatCurrency, formatCurrencyWithoutSign } from "src/helpers";


type InfoRowProps = {
  label: string;
  value: string | number | null | undefined;
  isCurrency?: boolean;
};

const InfoRow: FC<InfoRowProps> = ({ label, value, isCurrency = false }) => (
  <div className="col-12 col-md-6">
    <div className="row">
      <div className="col-6 text-end px-3">
        <p>{label}:</p>
      </div>
      <div className="col-6">
        <p>{value ? (isCurrency ? `$ ${value}` : value) : isCurrency ? "$ 0" : "N/A"}</p>
      </div>
    </div>
  </div>
);


const EOBsInformation: FC<EOBsObject> = ({ EOBsObject }): JSX.Element => {
  const [showPdf, setShowPdf] = useState<boolean>(false);
  const [selectedPdfId, setSelectedPdfId] = useState<number>();


  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        ['p', 's', 'u'].includes(e.key.toLowerCase())
      ) {
        e.preventDefault(); // Block print, save, view source
      }

      if (e.key === "Escape") {
        e.preventDefault(); // Block Esc key closing
      }

      // Optional: block F12
      if (e.key === "F12") {
        e.preventDefault();
      }
    };

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault(); // Disable right-click
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // Required for modern browsers to show confirm dialog
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("contextmenu", handleContextMenu);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("contextmenu", handleContextMenu);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);


  return (
    <div className="card">
      <div className="card-header">
        <h4 className="m-0">EOB Information</h4>
      </div>
      <div className="card-body">
        <div className="row">
          <InfoRow
            label="Claim Date"
            value={
              EOBsObject?.claim_date
                ? format(parse(EOBsObject.claim_date.split("T")[0], "yyyy-MM-dd", new Date()), "MM/dd/yyyy")
                : "N/A"
            }
          />
          <InfoRow label="Provider Speciality" value={EOBsObject?.provider_speciality} />
          <InfoRow label="Product" value={EOBsObject.product ? EOBsObject.product.replace(/Exparel/i, 'EXPAREL'):''} />
          <InfoRow label="Site of Care Type" value={EOBsObject?.soc} />
          <InfoRow label="Insurance Type" value={EOBsObject?.insurance_type? formatInsuranceType(EOBsObject?.insurance_type) : ''} />
          <InfoRow label="Insurance Name" value={EOBsObject?.insurance_name} />
          <InfoRow label="Insurance MAC" value={EOBsObject?.insurance_mac} />
          <InfoRow label="Coverage" value={EOBsObject?.coverage} />
        </div>

        <div className="row me-3">
          <table className="border border-gray-300 m-3 text-left">
            <thead>
              <tr className="bg-gray-100">
               <th className="border px-4 py-2 text-nowrap">CPT Code</th>
                <th className="border px-4 py-2 text-nowrap text-end">Billed Amount</th>
                <th className="border px-4 py-2 text-nowrap text-end">Allowed Amount</th>
                <th className="border px-4 py-2 text-nowrap text-end">Paid Amount</th>
                <th className="border px-4 py-2 text-nowrap text-end">Patient Responsibility</th>
                <th className="border px-4 py-2 text-nowrap">Coverage</th>
              </tr>
            </thead>
            <tbody>
              {EOBsObject.cpt_codes.map((item, index) => (
                <tr key={index}>
                  <td className="border px-4 py-2">{item.cpt_codes ? item.cpt_codes :""}</td>
                    <td className="border px-4 py-2 text-end">{item.billed ? formatCurrencyWithoutSign(item.billed) : ''}</td>
                    <td className="border px-4 py-2 text-end">{item.allowed ? formatCurrencyWithoutSign(item.allowed) :''}</td>
                    <td className="border px-4 py-2 text-end">{item.paid ? formatCurrencyWithoutSign(item.paid): ''}</td>
                    <td className="border px-4 py-2 text-end">{item.copay ? formatCurrencyWithoutSign(Number(item.copay)):''}</td>
                    <td className="border px-4 py-2">{item.coverage ? item.coverage :''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 font-semibold">
                 <td className="border px-4 py-2 text-right fw-bolder">Total</td>
              <td className="border px-4 py-2 text-end">
                {formatCurrency(EOBsObject.cpt_codes.reduce((sum, item) => sum + (item.billed || 0), 0))}
              </td>
              <td className="border px-4 py-2 text-end">
                {formatCurrency(EOBsObject.cpt_codes.reduce((sum, item) => sum + (item.allowed || 0), 0))}
              </td>
              <td className="border px-4 py-2 text-end">
                {formatCurrency(EOBsObject.cpt_codes.reduce((sum, item) => sum + (item.paid || 0), 0))}
              </td>
              <td className="border px-4 py-2 text-end">
                {formatCurrency(EOBsObject.cpt_codes.reduce((sum, item) => sum + (Number(item.copay) || 0), 0))}
              </td>
              <td className="border px-4 py-2 text-end"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="row mt-3">
          <div className="col-12 col-md-6">
            <div className="row">
              <div className="col-6 text-end px-3">
                <p className="mb-0">Redacted File:</p>
              </div>
                {EOBsObject?.redacted_file &&
                <div className="col-6">
                 <i className="bi bi-file-pdf text-danger fw-bold"></i>
                  <button
                    className="btn btn-link link-underline link-underline-opacity-0 link-offset-0-hover link-underline-opacity-75-hover p-0 align-baseline ps-1"
                    onClick={() => {
                      setSelectedPdfId(EOBsObject.id);
                      setShowPdf(true);
                    }}>View</button>
                     </div>
                }
             
            </div>
          </div>

          <InfoRow label="Practice State" value={EOBsObject?.practice_state} />


        </div>
        {
          showPdf && selectedPdfId && (
            <ViewPdf id={selectedPdfId} onClose={() => {
              setShowPdf(false);
              setSelectedPdfId(0);
            }} />
          )
        }
      </div >
    </div >

  );
};

export default EOBsInformation;

